/**
 * Scopes object helps to define the query keys better
 */
const scopes = {
  // For transaction Menu
  tx: "transaction",
  txIds: "transactionIds",
  txLightList: "transactionList",

  // For Patient Specific Transactions.
  patientBillsInfo: "patientBillsInfo", // patientBillInfoDto from getPaidBillsByPatientId
  patientTransactions: "patientTransactions", // BaseTransactionDetailsDto[]
  patientRechnungen: "patientRechnungen", // GetRechnungDetailsDto[]
  rxRechnungen: "rxRechnungen", // Individual rechnung for specific rx
  rxBillingTimeline: "rxBillingTimeline", // Billing timeline for a specific Rx
  debitPermissions: "debitPermissions", // DebitPermissionDto[]
  creditPermissions: "creditPermissions", // DebitPermissionDto[] (for refunds)
  rxDuePayment: "rxDuePayment",
  unpaidBadgeCount: "unpaidBadgeCount", // Count of unpaid Rxs for badge
  uniqueDebitPermissionPairs: "uniqueDebitPermissionPairs", // Unique (IBAN, BIC) pairs for a patient
  duePaymentByRx: "duePaymentByRx", // Due payment info for a specific RX
  rxDebit: "rxDebit", // RX-specific debit info from rx.getRxDebitById
  rxFeeWaivers: "rxFeeWaivers", // Waivable automatic fees of a specific Rx (e.g. PBE)

  // For Analytics
  executiveDashboard: "executiveDashboard", // ExecutiveDashboardDto
  kpiAnalytics: "kpiAnalytics", // KpiAnalyticsDto
  topPayersByAmount: "topPayersByAmount", // PayerPerformanceDto[]
  rxTypeDistribution: "rxTypeDistribution", // RxTypeDistributionDto[]
  trendAnalytics: "trendAnalytics", // TrendAnalyticsResponseDto

  // Insurance Export
  insuranceExport: "insuranceExport",
  edifactPayload: "edifactPayload",

  // Transaction Tickets
  transactionTickets: "transactionTickets",
};

export const transactionKeys = {
  // Info about the billing (Paid, Due) for a patient
  patientBillsInfo: {
    scope: scopes.patientBillsInfo,
    all: () => [transactionKeys.patientBillsInfo.scope] as const,
    forPatient: (patientId: number) =>
      [transactionKeys.patientBillsInfo.scope, { patientId }] as const,
  },

  // Transactions that have already been created. (Paid)
  patientTransactions: {
    scope: scopes.patientTransactions,
    all: () => [transactionKeys.patientTransactions.scope] as const,
    forPatient: (patientId: number) =>
      [transactionKeys.patientTransactions.scope, { patientId }] as const,
  },

  // Rechnungs that are already created
  patientRechnungen: {
    scope: scopes.patientRechnungen,
    all: () => [transactionKeys.patientRechnungen.scope] as const,
    forPatient: (patientId: number) => [
      transactionKeys.patientRechnungen.scope,
      { patientId },
    ],
  },

  // Rechnung that are related to specific Rx
  rxRechnungen: {
    scope: scopes.rxRechnungen,
    all: () => [transactionKeys.rxRechnungen.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.rxRechnungen.scope, { rxId }] as const,
  },

  // Billing timeline for a specific Rx (all transactions + rechnungen + reminders)
  rxBillingTimeline: {
    scope: scopes.rxBillingTimeline,
    all: () => [transactionKeys.rxBillingTimeline.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.rxBillingTimeline.scope, { rxId }] as const,
  },

  // Debit Permissions for SEPA
  debitPermissions: {
    scope: scopes.debitPermissions,
    all: () => [transactionKeys.debitPermissions.scope] as const,
    forPatient: (patientId: number) =>
      [transactionKeys.debitPermissions.scope, { patientId }] as const,
  },

  // Credit Permissions for SEPA Refunds
  creditPermissions: {
    scope: scopes.creditPermissions,
    all: () => [transactionKeys.creditPermissions.scope] as const,
    forPatient: (patientId: number) =>
      [transactionKeys.creditPermissions.scope, { patientId }] as const,
  },

  // Due Payment for RX
  rxDuePayment: {
    scope: scopes.rxDuePayment,
    all: () => [transactionKeys.rxDuePayment.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.rxDuePayment.scope, { rxId }] as const,
  },

  // Badge count for unpaid Rxs
  unpaidBadgeCount: {
    scope: scopes.unpaidBadgeCount,
    all: () => [transactionKeys.unpaidBadgeCount.scope] as const,
    forPatient: (patientId: number) =>
      [transactionKeys.unpaidBadgeCount.scope, { patientId }] as const,
  },
  uniqueDebitPermissionPairs: {
    scope: scopes.uniqueDebitPermissionPairs,
    all: () => [transactionKeys.uniqueDebitPermissionPairs.scope] as const,
    forPatient: (patientId: number) =>
      [
        transactionKeys.uniqueDebitPermissionPairs.scope,
        { patientId },
      ] as const,
  },
  duePaymentByRx: {
    scope: scopes.duePaymentByRx,
    all: () => [transactionKeys.duePaymentByRx.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.duePaymentByRx.scope, { rxId }] as const,
  },

  // RX-specific debit info (rx.getRxDebitById) - distinct from debitPermissions list
  rxDebit: {
    scope: scopes.rxDebit,
    all: () => [transactionKeys.rxDebit.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.rxDebit.scope, { rxId }] as const,
  },

  // Waivable automatic fees of a specific Rx (private assessment fee)
  rxFeeWaivers: {
    scope: scopes.rxFeeWaivers,
    all: () => [transactionKeys.rxFeeWaivers.scope] as const,
    forRx: (rxId: string) =>
      [transactionKeys.rxFeeWaivers.scope, { rxId }] as const,
  },

  // Insurance Export
  insuranceExport: {
    scope: scopes.insuranceExport,
    forTransaction: (transactionNumber: string) =>
      [transactionKeys.insuranceExport.scope, { transactionNumber }] as const,
    forTransactionId: (transactionId: string) =>
      [transactionKeys.insuranceExport.scope, { transactionId }] as const,
  },

  // EDIFACT Payload
  edifactPayload: {
    scope: scopes.edifactPayload,
    forExport: (exportId: string) =>
      [transactionKeys.edifactPayload.scope, { exportId }] as const,
  },

  // Analytics Keys
  executiveDashboard: {
    scope: scopes.executiveDashboard,
    all: () => [transactionKeys.executiveDashboard.scope] as const,
    withFilters: (filters: Record<string, any>) =>
      [transactionKeys.executiveDashboard.scope, filters] as const,
  },

  kpiAnalytics: {
    scope: scopes.kpiAnalytics,
    all: () => [transactionKeys.kpiAnalytics.scope] as const,
    withFilters: (filters: Record<string, any>) =>
      [transactionKeys.kpiAnalytics.scope, filters] as const,
  },

  topPayersByAmount: {
    scope: scopes.topPayersByAmount,
    all: () => [transactionKeys.topPayersByAmount.scope] as const,
    withFilters: (filters: Record<string, any>, limit?: number) =>
      [transactionKeys.topPayersByAmount.scope, { ...filters, limit }] as const,
  },

  rxTypeDistribution: {
    scope: scopes.rxTypeDistribution,
    all: () => [transactionKeys.rxTypeDistribution.scope] as const,
    withFilters: (filters: Record<string, any>) =>
      [transactionKeys.rxTypeDistribution.scope, filters] as const,
  },

  trendAnalytics: {
    scope: scopes.trendAnalytics,
    all: () => [transactionKeys.trendAnalytics.scope] as const,
    withFilters: (filters: Record<string, any>) =>
      [transactionKeys.trendAnalytics.scope, filters] as const,
  },

  transaction: {
    scope: scopes.tx,
    all: () => [transactionKeys.transaction.scope] as const,
    details: (transactionId: string) =>
      [transactionKeys.transaction.scope, { transactionId }] as const,
  },

  transactionTickets: {
    scope: scopes.transactionTickets,
    all: () => [transactionKeys.transactionTickets.scope] as const,
    forTransaction: (transactionId: string) =>
      [transactionKeys.transactionTickets.scope, { transactionId }] as const,
  },
};
